import { handleError } from '@services/errHandler';
import { coreAppStore } from '@stores/utility-stores/project';
import type { Project as Tenant, WebManifest } from '@vincerosoft/assessfluence-types';
import { registerCustomSurveyProps } from '@widgets/surveyjs/customProps';
import { registerCustomWidgets } from '@widgets/surveyjs/registerCustomWidgets';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { firebaseConfig } from 'src/config/env';
import { initializeFirebaseServices } from '../config/firebase';
import { setSentryTenantContext } from '../config/sentry';
import { themeContext } from '../stores/utility-stores/appTheme';

registerCustomSurveyProps();
registerCustomWidgets();

function toWebManifest(tenant: Tenant): WebManifest {
  return {
    background_color: tenant.background_color,
    display: tenant.display,
    icons: tenant.icons,
    name: tenant.name,
    short_name: tenant.short_name,
    start_url: tenant.start_url,
    theme_color: tenant.theme_color,
    theme: tenant.theme
  };
}

function updateManifest(manifest: WebManifest): void {
  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
  const manifestUrl = URL.createObjectURL(blob);

  const el = document.querySelector('#web-app-manifest-placeholder');
  if (el) {
    el.setAttribute('href', manifestUrl);
  }

  if (manifest.name) {
    document.title = manifest.name;
  }
}

async function fetchTenant(): Promise<Tenant | null> {
  const hostname = window.location.hostname;
  let endpoint = '/manifest.json';

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // the service does not exist in development mode
    endpoint = `https://${import.meta.env.VITE_FIREBASE_PROJECT_ID}.web.app/manifest.json`;
  }

  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      Accept: 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Unable to resolve tenant. HTTP ${response.status}`);
  }

  return response.json();
}

export async function bootstrapApp(): Promise<void> {
  let tenant: Tenant | null = null;

  const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

  try {
    tenant = await fetchTenant();
    if (!tenant) {
      throw new Error('Tenant not found');
    }

    updateManifest(toWebManifest(tenant));
    setSentryTenantContext(tenant);
    // Set the theme from the tenant
    coreAppStore.getState().setProject(tenant);
    const themeColor = tenant.theme?.theme_id || null;
    themeContext.getState().setThemeColor(themeColor);

    await initializeFirebaseServices(tenant, app);
  } catch (error) {
    const pathname = window.location.pathname;
    const isAuthRoute = pathname === '/auth' || pathname.startsWith('/__/auth');

    handleError(error, 'Tenant bootstrap failed');

    // Keep auth flows alive even when tenant manifest cannot be resolved.
    await initializeFirebaseServices(null, null);

    // Avoid hard-refresh loops when tenant resolution fails on auth routes.
    if (!isAuthRoute) {
      window.location.assign('/auth');
    }

    return;
  }
}
